import { useCallback } from "react";
import { useBooleanFlag } from "@apac/feature-flags";
import { type FiatPrice, Funnel } from "@b2bportal/core-types";
import type { AccountPrice } from "@b2bportal/hsbc-accounts-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FeatureFlagNames } from "@hsbc/utilities";
import { usePointsBalance } from "./usePointsBalance";

interface UsePayWithPointsPriceParams {
  funnel: Funnel;
}

interface UsePayWithPointsPriceResponse {
  getFormattedPayWithPointsMessage(price?: FiatPrice): string | null;
}

export const usePayWithPointsPrice = ({
  funnel,
}: UsePayWithPointsPriceParams): UsePayWithPointsPriceResponse => {
  const showMerchandisingPayWithPointsPrice = useBooleanFlag(
    FeatureFlagNames.SHOW_MERCHANDISING_PAY_WITH_POINTS_PRICE
  );
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();
  const { rewardsPoints, conversionMultiplier, cashEquivalent } =
    usePointsBalance();

  const getFormattedPayWithPointsMessage = useCallback(
    (price?: FiatPrice): string | null => {
      if (!showMerchandisingPayWithPointsPrice) {
        return null;
      }
      const pointsToFullyOffset: AccountPrice = {
        ...rewardsPoints,
        value: Math.ceil(price.value / conversionMultiplier),
      };
      const fullyOffsetable = pointsToFullyOffset.value <= rewardsPoints.value;

      const formattedPayWithPointsMessage = (() => {
        if (!fullyOffsetable) {
          return t("payWithPoints.partiallyOffsetable", {
            amount: formatFiatCurrency(cashEquivalent, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
            points: formatRewardsCurrency(rewardsPoints),
          });
        }

        switch (funnel) {
          case Funnel.Air:
            return t("payWithPoints.fullyOffsetableFlight", {
              points: formatRewardsCurrency(pointsToFullyOffset),
            });
          case Funnel.Lodging:
            return t("payWithPoints.fullyOffsetableHotel", {
              points: formatRewardsCurrency(pointsToFullyOffset),
            });
          case Funnel.Ground:
            return t("payWithPoints.fullyOffsetableCar", {
              points: formatRewardsCurrency(pointsToFullyOffset),
            });
          default:
            return null;
        }
      })();

      return formattedPayWithPointsMessage;
    },
    [
      cashEquivalent,
      conversionMultiplier,
      formatFiatCurrency,
      formatRewardsCurrency,
      funnel,
      rewardsPoints,
      showMerchandisingPayWithPointsPrice,
      t,
    ]
  );

  return {
    getFormattedPayWithPointsMessage,
  };
};
